import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import { actions } from '../../dispatch';
import { dispatch } from '../../dispatch/dispatch';
import ContextStore from '../../hooks/ContextStore';
import { ICreateGroupFormData } from '../../interfaces/formData/CreateGroupFormData.interface';
import { IErrorResponse } from '../../interfaces/responses/ErrorResponse.interface';
import { IProductGroupResponse } from '../../interfaces/responses/ProductGroupResponse.interface';
import { IProductResponse } from '../../interfaces/responses/ProductResponse.interface';

interface ICreateGroupProps {
  setShowModal: Dispatch<SetStateAction<boolean>>;
  products: Array<IProductResponse>;
}

export const CreateGroupModal: FC<ICreateGroupProps> = ({ setShowModal, products }) => {
  const { contextStore, setContextStore } = useContext(ContextStore);
  const ref = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<ICreateGroupFormData>({
    name: '',
    buyingRate: 0,
    storageFees: 0,
    transportFees: 0,
    packageFees: 0,
    jumiaCommission: 15,
    fraisTraitement: 6,
  });
  const [showForm, setShowForm] = useState<boolean>(true);

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (showForm && ref.current && !ref.current.contains(e.target)) {
        setShowForm(false);
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => document.removeEventListener('mousedown', checkIfClickedOutside);
  }, [showForm]);

  const onChangeFormData = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onClickCancel = (e: React.SyntheticEvent) => { e.preventDefault(); setShowModal(false); };

  const onClickAdd = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    contextStore.setShowSpinner(true);
    const productGroupResponse: IProductGroupResponse | IErrorResponse = await dispatch(
      actions.createProductGroup,
      { ...formData, productIds: products.map((p) => p._id), storeId: contextStore.store._id },
      contextStore.token
    );
    if ('error' in productGroupResponse) {
      setShowModal(false);
      contextStore.setShowSpinner(false);
      return console.log(productGroupResponse.error.message);
    }
    const productGroupProductsResponse: Array<IProductResponse> | IErrorResponse = await dispatch(
      actions.getProductGroupProducts,
      { fromDate: contextStore.date.fromDate, toDate: contextStore.date.toDate },
      contextStore.token,
      { productGroupId: productGroupResponse._id }
    );
    if ('error' in productGroupProductsResponse) {
      return console.log(productGroupProductsResponse.error.message);
    }
    const vProducts = contextStore.products;
    productGroupProductsResponse.map((product) => {
      const idx = vProducts.findIndex((vp) => vp._id === product._id);
      if (idx !== -1) vProducts[idx] = product;
    });
    let vProductGroups = contextStore.productGroups;
    vProductGroups.push(productGroupResponse);
    contextStore.overviewTrigger(vProducts);
    contextStore.productTrigger(vProducts);
    contextStore.productGroupTrigger(vProductGroups, vProducts);
    setContextStore((past: any) => ({ ...past, productGroups: vProductGroups, products: vProducts }));
    contextStore.setShowSpinner(false);
    setShowModal(false);
  };

  const fields = [
    { label: 'Group Name', name: 'name', type: 'text', value: formData.name, placeholder: 'e.g. Electronics' },
    { label: 'Buying Price', name: 'buyingRate', type: 'number', value: formData.buyingRate },
    { label: 'Transport Fees', name: 'transportFees', type: 'number', value: formData.transportFees },
    { label: 'Storage Fees', name: 'storageFees', type: 'number', value: formData.storageFees },
    { label: 'Package Fees', name: 'packageFees', type: 'number', value: formData.packageFees },
    { label: 'Jumia Commission (%)', name: 'jumiaCommission', type: 'number', value: formData.jumiaCommission },
    { label: 'Frais de traitement (DH)', name: 'fraisTraitement', type: 'number', value: formData.fraisTraitement },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <form ref={ref} onSubmit={onClickAdd}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Create Product Group</h2>
          {products.length > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {products.length} SKU{products.length !== 1 ? 's' : ''} will be added
            </p>
          )}
        </div>
        <div className="px-6 py-4 space-y-3">
          {fields.map(({ label, name, type, value, placeholder }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</label>
              <input
                type={type} name={name} value={value as any} placeholder={placeholder}
                onChange={onChangeFormData}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border bg-muted/30">
          <button type="button" onClick={onClickCancel}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">
            Cancel
          </button>
          <button type="submit"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
            Create Group
          </button>
        </div>
      </form>
    </div>,
    document.getElementById('createGroup')!
  );
};
